import React, { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { documentTypes } from '../documentTypes';
import { validateDocumentWithGemini } from '../services/geminiService';
import './DocumentUpload.css';

function DocumentUpload({ setValidationResult, setIsLoading }) {
    const [selectedDocType, setSelectedDocType] = useState(documentTypes[0].value);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [uploadMessage, setUploadMessage] = useState('');
    const [showCamera, setShowCamera] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [facingMode, setFacingMode] = useState("environment"); // Start with back camera on mobile

    const fileInputRef = useRef(null);
    const webcamRef = useRef(null);

    const handleFileChange = useCallback((file) => {
        console.log("handleFileChange called with:", {
            fileExists: !!file,
            fileType: file?.type,
            fileSize: file?.size,
            fileName: file?.name
        });

        if (!file) {
            console.log("No file provided to handleFileChange");
            return;
        }

        // Check if file has all required properties
        if (!file.type) {
            console.log("File missing type property");
            setUploadStatus('error');
            setUploadMessage('Invalid file: missing type information');
            setSelectedFile(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }

        if (file.size === undefined) {
            console.log("File missing size property");
            setUploadStatus('error');
            setUploadMessage('Invalid file: missing size information');
            setSelectedFile(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            console.log("File too large:", file.size);
            setUploadStatus('error');
            setUploadMessage('File is too large (max 10MB).');
            setSelectedFile(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }

        if (!file.type.startsWith('image/')) {
            console.log("Invalid file type:", file.type);
            setUploadStatus('error');
            setUploadMessage('Invalid file type. Please upload an image.');
            setSelectedFile(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }

        console.log("File validation passed, setting selected file");
        setSelectedFile(file);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(file));
        setUploadStatus('');
        setUploadMessage('');
    }, [previewUrl]);

    const onFileInputChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            handleFileChange(event.target.files[0]);
        }
    };

    const handleDocTypeChange = (event) => {
        setSelectedDocType(event.target.value);
    };

    const startCamera = () => {
        setShowCamera(true);
        setSelectedFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";

        setUploadStatus('info');
        setUploadMessage('Camera initializing... Please allow access if prompted.');
    };

    const stopCamera = () => {
        setShowCamera(false);
        setUploadStatus('');
        setUploadMessage('');
    };

    // Switch between front and back cameras
    const flipCamera = () => {
        setFacingMode(prevMode =>
            prevMode === "user" ? "environment" : "user"
        );
    };

    const takePicture = useCallback(() => {
        if (!webcamRef.current) {
            setUploadStatus('error');
            setUploadMessage('Camera not properly initialized.');
            return;
        }

        try {
            const imageSrc = webcamRef.current.getScreenshot();
            if (!imageSrc) {
                setUploadStatus('error');
                setUploadMessage('Failed to capture image. Please try again.');
                return;
            }

            // Convert the base64 image to a File object
            fetch(imageSrc)
                .then(res => {
                    if (!res.ok) {
                        throw new Error(`Failed to process image: ${res.status} ${res.statusText}`);
                    }
                    return res.blob();
                })
                .then(blob => {
                    if (!blob || blob.size === 0) {
                        throw new Error('Captured image is empty');
                    }

                    try {
                        const fileName = `capture-${new Date().toISOString()}.jpg`;
                        const file = new File([blob], fileName, { type: 'image/jpeg' });

                        // Verify the file was created properly
                        if (!file || file.size === 0) {
                            throw new Error('Failed to create file from captured image');
                        }

                        handleFileChange(file);
                        stopCamera();
                    } catch (fileError) {
                        console.error("Error creating file:", fileError);
                        setUploadStatus('error');
                        setUploadMessage('Failed to create file from captured image. Please try again.');
                    }
                })
                .catch(err => {
                    console.error("Error processing captured image:", err);
                    setUploadStatus('error');
                    setUploadMessage('Failed to process image. Please try again.');
                });
        } catch (err) {
            console.error("Error capturing image:", err);
            setUploadStatus('error');
            setUploadMessage('Failed to capture image. Please try again.');
        }
    }, [webcamRef, handleFileChange, stopCamera]);

    // Form ref for handling form submission
    const formRef = useRef(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        console.log("Form submitted with:", {
            fileExists: !!selectedFile,
            fileType: selectedFile?.type,
            fileSize: selectedFile?.size,
            documentType: selectedDocType
        });

        if (!selectedFile || !selectedDocType) {
            console.log("Missing file or document type");
            setUploadStatus('error');
            setUploadMessage('Please select a document type and provide an image.');
            return;
        }

        // Validate file before processing
        if (!selectedFile.type || !selectedFile.type.startsWith('image/')) {
            console.log("Invalid file type:", selectedFile.type);
            setUploadStatus('error');
            setUploadMessage('Please select a valid image file');
            return;
        }

        // Additional validation
        if (selectedFile.size === undefined || selectedFile.size === 0) {
            console.log("File size issue:", { size: selectedFile.size });
            setUploadStatus('error');
            setUploadMessage('Invalid file: file size is zero or undefined');
            return;
        }

        console.log("File validation passed, proceeding with upload");
        setIsUploading(true);
        setIsLoading(true);
        setUploadStatus('info');
        setUploadMessage('Processing your document...');

        try {
            console.log("Calling validateDocumentWithGemini with:", {
                fileType: selectedFile.type,
                fileSize: selectedFile.size,
                fileName: selectedFile.name,
                documentType: selectedDocType
            });

            // Call the Gemini AI service to validate the document
            const result = await validateDocumentWithGemini(selectedFile, selectedDocType);
            console.log("Received result from Gemini:", result);

            // Set the validation result to be displayed in the ValidationResponse component
            setValidationResult(result);

            setUploadStatus('success');
            setUploadMessage(`Document processed successfully!`);

            // Clear the selected file and preview
            setSelectedFile(null);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
            console.error("Error processing document:", error);
            console.log("Error details:", {
                name: error.name,
                message: error.message,
                stack: error.stack
            });

            setUploadStatus('error');
            setUploadMessage(`Processing failed: ${error.message}`);
            setValidationResult(null);
        } finally {
            setIsUploading(false);
            setIsLoading(false);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // Handle webcam loading success
    const handleWebcamUserMedia = useCallback(() => {
        console.log("Webcam accessed successfully");
        setUploadStatus('');
        setUploadMessage('');
    }, []);

    // Handle webcam errors
    const handleWebcamError = useCallback((error) => {
        console.error("Webcam error:", error);
        setUploadStatus('error');
        setUploadMessage(
            error.name === 'NotAllowedError'
                ? 'Camera access denied. Please allow camera access and try again.'
                : 'Camera error: ' + (error.message || 'Failed to access camera')
        );
    }, []);



    const videoConstraints = {
        width: 1280,
        height: 720,
        facingMode: facingMode
    };

    return (
        <div className="box">
            <div className="has-text-centered mb-4">
                <h2>Upload</h2>
            </div>

            <form onSubmit={handleSubmit} ref={formRef}>
                <div className="field">
                    <label className="label">
                        <i className="fas fa-file-alt mr-2"></i>
                        <span>Document Type</span>
                    </label>
                    <div className="select is-fullwidth">
                        <select
                            value={selectedDocType}
                            onChange={handleDocTypeChange}
                            disabled={isUploading || showCamera}
                        >
                            {documentTypes.map(doc => (
                                <option key={doc.value} value={doc.value}>{doc.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div
                    className={`file-drop-zone ${isDragging ? 'is-primary' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => {if (!showCamera && !isUploading) triggerFileInput()}}
                >
                    <div className="has-text-centered">
                        <i className="fas fa-cloud-upload-alt fa-2x has-text-primary"></i>
                        <p className="mt-3 mb-3">
                            <span>Drop files here</span>
                        </p>

                        <div className="buttons is-centered mt-4">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); triggerFileInput(); }}
                                disabled={isUploading || showCamera}
                                className="button is-primary"
                            >
                                <i className="fas fa-file-upload mr-2"></i>
                                <span>Choose File</span>
                            </button>
                            {!showCamera && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); startCamera(); }}
                                    disabled={isUploading}
                                    className="button is-info"
                                >
                                    <i className="fas fa-camera mr-2"></i>
                                    <span>Take Picture</span>
                                </button>
                            )}
                        </div>
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={onFileInputChange}
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        disabled={isUploading || showCamera}
                    />
                </div>

                {showCamera && (
                    <div className="box">
                        <div className="camera-container">
                            <Webcam
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                videoConstraints={videoConstraints}
                                className="camera-feed"
                                style={{ width: '100%', height: '300px', objectFit: 'contain' }}
                                onUserMedia={handleWebcamUserMedia}
                                onUserMediaError={handleWebcamError}
                            />

                            <div className="buttons is-centered mt-4">
                                <button
                                    type="button"
                                    onClick={takePicture}
                                    disabled={isUploading || uploadStatus === 'error'}
                                    className="button is-primary"
                                >
                                    <i className="fas fa-camera mr-2"></i>
                                    <span>Capture Photo</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={flipCamera}
                                    disabled={isUploading}
                                    className="button is-info"
                                >
                                    <i className="fas fa-sync mr-2"></i>
                                    <span>Flip Camera</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={stopCamera}
                                    disabled={isUploading}
                                    className="button is-danger is-light"
                                >
                                    <i className="fas fa-times mr-2"></i>
                                    <span>Cancel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {previewUrl && !showCamera && (
                    <div className="box">
                        <p className="subtitle is-6 has-text-centered mb-3">
                            <span>Preview</span>
                        </p>
                        <figure className="image has-text-centered">
                            <img src={previewUrl} alt="Document preview" className="preview-image" />
                        </figure>
                    </div>
                )}

                {selectedFile && !showCamera && (
                    <p className="help has-text-centered mt-2">
                        <i className="fas fa-file-alt mr-2"></i>
                        <span>{selectedFile.name}</span>
                    </p>
                )}

                <div className="field mt-5">
                    <div className="control">
                        <button
                            type="submit"
                            disabled={!selectedFile || isUploading || showCamera}
                            className={`button is-primary is-fullwidth ${isUploading ? 'is-loading' : ''}`}
                        >
                            <i className="fas fa-lock mr-2"></i>
                            <span>{isUploading ? 'Processing Document...' : 'Validate Document'}</span>
                        </button>
                    </div>
                </div>
            </form>

            {uploadMessage && (
                <div className={`notification ${
                    uploadStatus === 'success' ? 'is-success' :
                    uploadStatus === 'error' ? 'is-danger' :
                    'is-info'
                } mt-4`}>
                    <i className={`fas mr-2 ${
                        uploadStatus === 'success' ? 'fa-check-circle' :
                        uploadStatus === 'error' ? 'fa-exclamation-circle' :
                        'fa-info-circle'
                    }`}></i>
                    <span>{uploadMessage}</span>
                </div>
            )}
        </div>
    );
}

export default DocumentUpload;